import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const BookReviewContainer = styled.div`
  padding: 6rem 1.5rem 5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: rgb(var(--bg));
  min-height: 100vh;
  font-family: 'Arial', sans-serif;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 10% 0%, rgba(241,90,34,0.12), transparent 40%);
    pointer-events: none;
  }

  & > * {
    position: relative;
    z-index: 1;
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: #f15a22;
  text-align: center;
  margin-bottom: 1rem;
  line-height: 1.2;
`;

const Author = styled.p`
  font-size: 1.2rem;
  color: rgb(var(--fg) / 0.78);
  margin-bottom: 1rem;
  font-style: italic;
`;

const CoverImage = styled.img`
  width: 100%;
  max-width: 300px;
  max-height: 600px;
  border-radius: 8px;
  margin-bottom: 1rem;
  box-shadow: 0 20px 40px rgb(0 0 0 / calc(0.5 * var(--shadow-mul)));
  border: 1px solid rgb(var(--fg) / 0.08);
`;

const DatePublished = styled.p`
  font-size: 1rem;
  color: rgb(var(--gray) / 0.55);
  margin-bottom: 2rem;
`;

const ContentSection = styled.div`
  width: 100%;
  max-width: 1200px;
  margin-bottom: 2rem;
  background: rgb(var(--fg) / 0.035);
  border: 1px solid rgb(var(--fg) / 0.06);
  padding: 2rem;
  border-radius: 20px;
  backdrop-filter: blur(24px) saturate(1.15);
  -webkit-backdrop-filter: blur(24px) saturate(1.15);
  box-shadow: 0 24px 56px -16px rgb(0 0 0 / calc(0.5 * var(--shadow-mul)));
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  margin-bottom: 1rem;
  color: #f15a22;
  border-bottom: 1px solid rgba(241, 90, 34, 0.3);
  padding-bottom: 0.5rem;
`;

const SectionContent = styled.div`
  font-size: 1rem;
  line-height: 1.8;
  color: rgb(var(--fg) / 0.82);
`;

const Quote = styled.blockquote`
  font-style: italic;
  margin: 1.5rem 0;
  padding: 1rem;
  background: rgb(var(--fg) / 0.04);
  border-left: 3px solid #f15a22;
  border-radius: 8px;
  color: rgb(var(--fg) / 0.78);
`;

const ListItem = styled.li`
  margin-bottom: 1rem;
  font-size: 1rem;
  color: rgb(var(--fg) / 0.82);
`;

const OrderedList = styled.ol`
  padding-left: 1rem;
  color: rgb(var(--fg) / 0.82);
`;

const TagContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  max-width: 1200px;
  gap: 0.5rem;
  margin-top: 1.5rem;
`;

const Tag = styled.span`
  background: rgb(var(--fg) / 0.05);
  border: 1px solid rgba(241, 90, 34, 0.3);
  color: rgb(var(--fg) / 0.9);
  padding: 0.4rem 0.7rem;
  border-radius: 12px;
  font-size: 0.8rem;
  backdrop-filter: blur(4px);
`;

const Button = styled.button`
  display: inline-block;
  margin: 2rem 0;
  background: rgb(var(--fg) / 0.06);
  color: rgb(var(--fg) / 0.92);
  border: 1px solid rgb(var(--fg) / 0.14);
  padding: 0.9rem 2rem;
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
  backdrop-filter: blur(12px);
  transition: transform 0.25s, border-color 0.25s, background 0.25s;
  text-align: center;

  &:hover {
    transform: translateY(-1px);
    background: rgb(var(--fg) / 0.1);
    border-color: rgba(241, 90, 34, 0.5);
  }
`;

export const OutliersMetadata = {
  title: "Outliers",
  description: "The Story of Success",
  tags: ['Psychology', 'Success', 'Culture', 'Sociology'],
  author: "Malcolm Gladwell",
  datePublished: "2024-03-26",
  category: "Book Review",
  path: "/book-review/outliers",
  coverImage: "/images/outliers.jpg",
};

const Outliers = () => {
  const navigate = useNavigate();
  const metadata = OutliersMetadata;

  return (
    <BookReviewContainer>
      <Title>{metadata.title}</Title>
      <Author>by {metadata.author}</Author>
      <CoverImage src={metadata.coverImage} alt={metadata.title} />
      <DatePublished>Date Published: {metadata.datePublished}</DatePublished>
      <TagContainer>
        {metadata.tags.map((tag, index) => (
          <Tag key={index}>{tag}</Tag>
        ))}
      </TagContainer>
      <br />
      <br />
      <ContentSection>
        <SectionTitle>Summary</SectionTitle>
        <SectionContent>
          <p>
            "Outliers" challenges the conventional wisdom about success by looking beyond individual talent. Malcolm Gladwell explores how circumstances, timing, and even cultural background can play a significant role in shaping success stories. Through compelling anecdotes and research, Gladwell argues that success is not just about innate talent but also about seizing the right opportunities.
          </p>
          <br />
          <p>
            By examining the lives of famous "outliers"—people whose achievements are far outside the norm—Gladwell uncovers patterns and hidden forces that contribute to success. The book raises critical questions about fairness, opportunity, and the factors that truly make a difference in a person's ability to achieve greatness.
          </p>
        </SectionContent>
      </ContentSection>

      <ContentSection>
        <SectionTitle>Key Themes or Concepts</SectionTitle>
        <SectionContent>
          <OrderedList>
            <ListItem><strong>10,000-Hour Rule:</strong> Gladwell popularizes the idea that it takes 10,000 hours of practice to achieve mastery in any field.</ListItem>
            <ListItem><strong>Opportunities and Timing:</strong> The book emphasizes how crucial opportunities and timing are in determining success.</ListItem>
            <ListItem><strong>Culture and Background:</strong> Gladwell discusses how cultural legacies and backgrounds shape an individual’s success trajectory.</ListItem>
            <ListItem><strong>Hidden Advantages:</strong> *Outliers* examines how unseen advantages, like birthdate or geography, can impact success.</ListItem>
            <ListItem><strong>Achievement vs. Luck:</strong> Gladwell shows how luck and circumstances play a larger role than we often acknowledge in stories of success.</ListItem>
          </OrderedList>
        </SectionContent>
      </ContentSection>

      <ContentSection>
        <SectionTitle>Favorite Quotes</SectionTitle>
        <SectionContent>
          <Quote>
            "Success is not a random act. It arises out of a predictable and powerful set of circumstances and opportunities."
          </Quote>
          <Quote>
            "The people who stand before kings may look like they did it all by themselves. But in fact, they are invariably the beneficiaries of hidden advantages and extraordinary opportunities."
          </Quote>
        </SectionContent>
      </ContentSection>

      <ContentSection>
        <SectionTitle>Personal Reflection</SectionTitle>
        <SectionContent>
          <p>
            "Outliers" made me rethink how we define success. The stories in the book clearly demonstrate that talent alone isn’t enough—there are many factors, like timing, cultural legacy, and access to opportunities, that have a profound effect on success. This is strongly connected to the concept of the "adjacent possible" from <a href="/book-review/where-good-ideas-come-from">"Where Good Ideas Come From"</a> by Steven Johnson, which highlights how new ideas and breakthroughs depend on the set of opportunities available at a given moment.
          </p>
          <br />
          <p>
            Just as the "adjacent possible" shows that innovation happens by building on what already exists and what is nearby in terms of opportunities, "Outliers" reinforces the idea that seizing the right opportunity at the right time is key to success. It’s not just about hard work—having the right conditions around you allows you to access your full potential.
          </p>
          <br />
          <p>
            This book reinforced my belief that we need to create environments where everyone has access to the right opportunities, not just those who are lucky enough to be born into them. It offers a powerful critique of the myths we tell about success and underlines the importance of broader societal responsibility in fostering achievement.
          </p>
        </SectionContent>
      </ContentSection>

      <Button onClick={() => navigate('/blog')}>Back to Blog</Button>
    </BookReviewContainer>
  );
};

export default Outliers;
